import type { IngredientLine, StepLine } from "./types";

// Mirrors api/src/lib/calc.ts so the builder can show live numbers without a
// network round trip on every keystroke. Kept intentionally identical.
const SUGAR_ML_PER_G = 0.625;
const GRAVITY_POINTS_PER_G_PER_L = 0.384;
const GRAVITY_TO_ABV_FACTOR = 131.25;

function toMl(value: number, unit: string): number {
  if (unit === "L") return value * 1000;
  if (unit === "ml") return value;
  return 0;
}
function toGrams(value: number, unit: string): number {
  if (unit === "kg") return value * 1000;
  if (unit === "g") return value;
  return 0;
}

export interface ComputedRecipe {
  abvPercent: number;
  yieldMl: number;
  sugarGPerL: number;
  originalGravity: number | null;
  finalGravity: number | null;
}

export function computeRecipe(ingredients: IngredientLine[]): ComputedRecipe {
  let totalVolumeMl = 0;
  let totalAlcoholMl = 0;
  let totalSugarG = 0;
  let totalFermentableSugarG = 0;
  const attenuations: number[] = [];

  for (const ing of ingredients) {
    if (ing.kind === "spirit") {
      const vol = toMl(ing.amountValue, ing.amountUnit);
      totalVolumeMl += vol;
      totalAlcoholMl += (vol * (ing.abvPercent ?? 0)) / 100;
    } else if (ing.kind === "water") {
      totalVolumeMl += toMl(ing.amountValue, ing.amountUnit);
    } else if (ing.kind === "sugar") {
      const grams = toGrams(ing.amountValue, ing.amountUnit);
      totalSugarG += grams;
      totalVolumeMl += grams * SUGAR_ML_PER_G;
    } else if (ing.kind === "fermentable") {
      const pct = (ing.sugarContentPercent ?? 100) / 100;
      const liquidMl = toMl(ing.amountValue, ing.amountUnit);
      if (liquidMl > 0) {
        totalVolumeMl += liquidMl;
        totalFermentableSugarG += liquidMl * pct;
      } else {
        const sugarG = toGrams(ing.amountValue, ing.amountUnit) * pct;
        totalFermentableSugarG += sugarG;
        totalVolumeMl += sugarG * SUGAR_ML_PER_G;
      }
    } else if (ing.kind === "yeast") {
      if (typeof ing.attenuationPercent === "number") attenuations.push(ing.attenuationPercent);
      totalVolumeMl += toMl(ing.amountValue, ing.amountUnit);
    } else {
      totalVolumeMl += toMl(ing.amountValue, ing.amountUnit);
    }
  }

  let originalGravity: number | null = null;
  let finalGravity: number | null = null;
  if (totalFermentableSugarG > 0 && totalVolumeMl > 0) {
    const volumeL = totalVolumeMl / 1000;
    const gravityPoints = (GRAVITY_POINTS_PER_G_PER_L * totalFermentableSugarG) / volumeL;
    const attenuation = attenuations.length ? attenuations.reduce((a, b) => a + b, 0) / attenuations.length : 0;
    const ogRaw = 1 + gravityPoints / 1000;
    const fgRaw = 1 + (gravityPoints * (1 - attenuation / 100)) / 1000;
    totalAlcoholMl += totalVolumeMl * (((ogRaw - fgRaw) * GRAVITY_TO_ABV_FACTOR) / 100);
    originalGravity = Math.round(ogRaw * 1000) / 1000;
    finalGravity = Math.round(fgRaw * 1000) / 1000;
  }

  const abvPercent = totalVolumeMl > 0 ? (totalAlcoholMl / totalVolumeMl) * 100 : 0;
  const sugarGPerL = totalVolumeMl > 0 ? totalSugarG / (totalVolumeMl / 1000) : 0;

  return {
    abvPercent: Math.round(abvPercent * 10) / 10,
    yieldMl: Math.round(totalVolumeMl),
    sugarGPerL: Math.round(sugarGPerL),
    originalGravity,
    finalGravity,
  };
}

export function totalDays(steps: StepLine[]): number {
  return steps.reduce((max, s) => Math.max(max, s.dayOffsetEnd), 0);
}
